import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    IconButton,
    LinearProgress,
    MenuItem,
    Modal,
    Paper,
    Slide,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Theme,
    Typography,
} from '@material-ui/core'
import { Close } from '@material-ui/icons'
import { DateTime } from 'luxon'
import { toast } from 'react-toastify'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Container } from '../../../../components'
import { axiosInstance } from '../../../../helpers'

interface SourceCandidate {
    id: number
    name: string
    schedule_id: string
    start_date: string | null
    end_date: string | null
    included_unit_count: number | null
    phase_count: number | null
    day_off_pattern: string
}

interface ScheduleCandidate {
    id: number
    name: string
    schedule_id: string
    apartment_id: number
    apartment_name: string
    review_status: string
    start_date: string | null
    end_date: string | null
    source_status: 'ready' | 'needs_selection' | 'no_source'
    suggested_source_id: number | null
    skip_reason: string | null
    sources: SourceCandidate[]
}

interface CandidatesResponse {
    year: number
    summary: {
        total: number
        ready: number
        needs_selection: number
        no_source: number
    }
    results: ScheduleCandidate[]
}

interface ScheduledItem {
    schedule_id: number
    apartment_id: number
    apartment_name: string
    name?: string
    source_name?: string
}

interface SkippedItem {
    schedule_id?: number
    apartment_id?: number
    apartment_name?: string
    name?: string
    reason: string
}

interface ErrorItem {
    schedule_id?: number
    apartment_id?: number
    apartment_name?: string
    name?: string
    error: string
}

interface StartTaskResponse {
    task_id: string
    total: number
    message: string
}

interface TaskStatusResponse {
    state: string
    current?: number
    total?: number
    apartment_name?: string | null
    scheduled?: ScheduledItem[]
    skipped?: SkippedItem[]
    errors?: ErrorItem[]
    result?: {
        scheduled: ScheduledItem[]
        skipped: SkippedItem[]
        errors: ErrorItem[]
    }
    error?: string
}

type ModalPhase = 'form' | 'running' | 'done' | 'failed'

interface Props {
    open: boolean
    theme: Theme
    onClose: () => void
    initialYear?: number
    /** Prefill / preselect these target schedule IDs. */
    preferredScheduleIds?: number[]
    /** Only show preferred schedules (selection already done in parent). */
    restrictToPreferred?: boolean
    /** Disable year changes (year controlled by parent panel). */
    lockYear?: boolean
}

const POLL_INTERVAL_MS = 1500

function currentTurnYear(): number {
    return DateTime.now().year
}

function formatDate(iso: string | null): string {
    if (!iso) return '—'
    const dt = DateTime.fromISO(iso)
    return dt.isValid ? dt.toFormat('LLL d, yyyy') : '—'
}

/** Ready to bulk-schedule with a single suggested source (Select All / eligible). */
function isReady(row: ScheduleCandidate): boolean {
    return row.source_status === 'ready' && row.sources.length > 0
}

/** Row checkbox enabled — Ready, or Pick source after a prior turn exists. */
function isSelectable(row: ScheduleCandidate): boolean {
    if (row.source_status === 'no_source') return false
    if (row.sources.length === 0) return false
    return (
        row.source_status === 'ready' || row.source_status === 'needs_selection'
    )
}

function defaultSourceId(row: ScheduleCandidate): number | null {
    if (row.suggested_source_id != null) {
        return row.suggested_source_id
    }
    return row.sources[0]?.id ?? null
}

export const BulkScheduleProjectsModal = (props: Props) => {
    const {
        open,
        theme,
        onClose,
        initialYear,
        preferredScheduleIds,
        restrictToPreferred,
        lockYear,
    } = props
    const queryClient = useQueryClient()

    const [year, setYear] = useState(initialYear ?? currentTurnYear())
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [sourceByTarget, setSourceByTarget] = useState<
        Record<number, number>
    >({})
    const [phase, setPhase] = useState<ModalPhase>('form')
    const [progressCurrent, setProgressCurrent] = useState(0)
    const [progressTotal, setProgressTotal] = useState(0)
    const [currentApartmentName, setCurrentApartmentName] = useState<
        string | null
    >(null)
    const [scheduled, setScheduled] = useState<ScheduledItem[]>([])
    const [skipped, setSkipped] = useState<SkippedItem[]>([])
    const [errors, setErrors] = useState<ErrorItem[]>([])
    const [failureMessage, setFailureMessage] = useState<string | null>(null)

    const pollIntervalRef = useRef<number | null>(null)
    const invalidatedOnCompleteRef = useRef(false)
    const selectionSeedKeyRef = useRef('')

    useEffect(() => {
        if (open && initialYear != null) {
            setYear(initialYear)
        }
    }, [open, initialYear])

    const candidatesQuery = useQuery({
        queryKey: ['bulk-schedule-candidates', year],
        queryFn: async () => {
            const response = await axiosInstance.get<CandidatesResponse>(
                '/workorder/org-project-analytics/bulk-schedule-term-projects/candidates/',
                { params: { year } },
            )
            return response.data
        },
        enabled: open && phase === 'form',
    })

    const allRows = candidatesQuery.data?.results ?? []
    const preferredSet = useMemo(() => new Set(preferredScheduleIds ?? []), [
        preferredScheduleIds,
    ])
    const rows = useMemo(() => {
        if (!restrictToPreferred || preferredSet.size === 0) {
            return allRows
        }
        return allRows.filter((row) => preferredSet.has(row.id))
    }, [allRows, restrictToPreferred, preferredSet])
    const preferredKey = (preferredScheduleIds ?? []).join(',')
    const selectionSeedKey = `${open}:${year}:${rows
        .map((r) => r.id)
        .join(',')}:${preferredKey}:${restrictToPreferred ? '1' : '0'}`

    const stopPolling = () => {
        if (pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current)
            pollIntervalRef.current = null
        }
    }

    const resetJobState = () => {
        stopPolling()
        setPhase('form')
        setProgressCurrent(0)
        setProgressTotal(0)
        setCurrentApartmentName(null)
        setScheduled([])
        setSkipped([])
        setErrors([])
        setFailureMessage(null)
        invalidatedOnCompleteRef.current = false
    }

    useEffect(() => {
        if (!open) {
            resetJobState()
            selectionSeedKeyRef.current = ''
            return
        }
        if (!candidatesQuery.data) return
        if (selectionSeedKeyRef.current === selectionSeedKey) return
        selectionSeedKeyRef.current = selectionSeedKey

        const nextSources: Record<number, number> = {}
        const nextSelected = new Set<number>()
        const preferred = preferredSet

        for (const row of rows) {
            const sourceId = defaultSourceId(row)
            if (sourceId != null) {
                nextSources[row.id] = sourceId
            }
            // Auto-select / Select All only include Ready rows.
            if (!isReady(row)) continue

            if (restrictToPreferred || preferred.size > 0) {
                if (preferred.size === 0 || preferred.has(row.id)) {
                    nextSelected.add(row.id)
                }
            } else {
                nextSelected.add(row.id)
            }
        }

        setSourceByTarget(nextSources)
        setSelectedIds(nextSelected)
    }, [
        open,
        selectionSeedKey,
        candidatesQuery.data,
        rows,
        preferredScheduleIds,
        preferredSet,
        restrictToPreferred,
    ])

    useEffect(() => stopPolling, [])

    /** Select All / eligible count — Ready only (not "No prior turn" / "Pick source"). */
    const eligibleRows = useMemo(() => rows.filter((row) => isReady(row)), [
        rows,
    ])

    const selectedCount = rows.filter((r) => selectedIds.has(r.id)).length
    const eligibleCount = eligibleRows.length

    const invalidateAnalytics = async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['workspace-project-analytics'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['project-analytics'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['project-drafts'],
                exact: false,
            }),
            queryClient.invalidateQueries({
                queryKey: ['bulk-schedule-candidates'],
                exact: false,
            }),
        ])
    }

    const toggleRow = (scheduleId: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (checked) {
                next.add(scheduleId)
            } else {
                next.delete(scheduleId)
            }
            return next
        })
    }

    const toggleAll = (checked: boolean) => {
        if (!checked) {
            setSelectedIds(new Set())
            return
        }
        setSelectedIds(new Set(eligibleRows.map((r) => r.id)))
    }

    const applyProgressPayload = (status: TaskStatusResponse) => {
        if (status.current !== undefined) {
            setProgressCurrent(status.current)
        }
        if (status.total !== undefined) {
            setProgressTotal(status.total)
        }
        if (status.apartment_name !== undefined) {
            setCurrentApartmentName(status.apartment_name)
        }
        if (status.scheduled) {
            setScheduled(status.scheduled)
        }
        if (status.skipped) {
            setSkipped(status.skipped)
        }
        if (status.errors) {
            setErrors(status.errors)
        }
    }

    const handleSchedule = async () => {
        const items = rows
            .filter((row) => selectedIds.has(row.id))
            .map((row) => {
                const sourceId = sourceByTarget[row.id] ?? defaultSourceId(row)
                return {
                    schedule_id: row.id,
                    source_schedule_id: sourceId,
                    apartment_name: row.apartment_name,
                }
            })
            .filter(
                (
                    item,
                ): item is {
                    schedule_id: number
                    source_schedule_id: number
                    apartment_name: string
                } => item.source_schedule_id != null,
            )

        if (items.length === 0) {
            toast.error('Select at least one project with a source turn')
            return
        }

        setPhase('running')
        setProgressCurrent(0)
        setProgressTotal(items.length)
        setCurrentApartmentName(null)
        setScheduled([])
        setSkipped([])
        setErrors([])
        setFailureMessage(null)
        invalidatedOnCompleteRef.current = false

        try {
            const response = await axiosInstance.post<StartTaskResponse>(
                '/workorder/org-project-analytics/bulk-schedule-term-projects/',
                {
                    items: items.map(({ schedule_id, source_schedule_id }) => ({
                        schedule_id,
                        source_schedule_id,
                    })),
                },
            )
            const { task_id, total } = response.data
            setProgressTotal(total)

            pollIntervalRef.current = window.setInterval(async () => {
                try {
                    const statusRes = await axiosInstance.get<TaskStatusResponse>(
                        '/workorder/org-project-analytics/bulk-schedule-term-projects/status/',
                        { params: { task_id } },
                    )
                    const status = statusRes.data
                    applyProgressPayload(status)

                    if (status.state === 'SUCCESS') {
                        stopPolling()
                        const result = status.result
                        setScheduled(
                            result?.scheduled ?? status.scheduled ?? [],
                        )
                        setSkipped(result?.skipped ?? status.skipped ?? [])
                        setErrors(result?.errors ?? status.errors ?? [])
                        setPhase('done')
                        if (!invalidatedOnCompleteRef.current) {
                            invalidatedOnCompleteRef.current = true
                            void invalidateAnalytics()
                        }
                    } else if (status.state === 'FAILURE') {
                        stopPolling()
                        setFailureMessage(
                            status.error ?? 'Bulk schedule failed',
                        )
                        setPhase('failed')
                    }
                } catch {
                    // Keep polling — a transient network blip should not
                    // mark a still-running Celery job as failed.
                }
            }, POLL_INTERVAL_MS)
        } catch {
            setFailureMessage('Failed to start bulk schedule')
            setPhase('failed')
        }
    }

    const thisYear = currentTurnYear()
    const yearOptions = [thisYear, thisYear + 1]
    const progressPercent =
        progressTotal > 0
            ? Math.min(100, Math.round((progressCurrent / progressTotal) * 100))
            : 0
    const showProgressView = phase !== 'form'
    const allEligibleChecked =
        eligibleRows.length > 0 &&
        eligibleRows.every((r) => selectedIds.has(r.id))

    const handleClose = () => {
        stopPolling()
        onClose()
    }

    const statusLabel = (row: ScheduleCandidate) => {
        if (row.source_status === 'no_source') return 'No prior turn'
        if (row.source_status === 'needs_selection') return 'Pick source'
        return 'Ready'
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            closeAfterTransition
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        width: 'min(900px, calc(100vw - 64px))',
                        height: 'calc(100vh - 64px)',
                        maxHeight: 'calc(100vh - 64px)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <Container
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: theme.spacing(1.5, 2),
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <Typography variant="h6">
                            Bulk Schedule Projects
                        </Typography>
                        <IconButton onClick={handleClose} size="small">
                            <Close />
                        </IconButton>
                    </Container>

                    {!showProgressView ? (
                        <>
                            <Box
                                style={{
                                    padding: theme.spacing(2),
                                    borderBottom:
                                        '1px solid rgba(0, 0, 0, 0.08)',
                                }}
                            >
                                {!lockYear ? (
                                    <>
                                        <Typography
                                            variant="subtitle1"
                                            style={{
                                                fontWeight: 700,
                                                marginBottom: theme.spacing(
                                                    0.75,
                                                ),
                                            }}
                                        >
                                            Turn Year
                                        </Typography>
                                        <TextField
                                            select
                                            value={year}
                                            onChange={(e) =>
                                                setYear(Number(e.target.value))
                                            }
                                            size="small"
                                            style={{ minWidth: 120 }}
                                            variant="outlined"
                                        >
                                            {yearOptions.map((y) => (
                                                <MenuItem key={y} value={y}>
                                                    {y}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </>
                                ) : (
                                    <Typography
                                        variant="subtitle1"
                                        style={{
                                            marginBottom: theme.spacing(1),
                                            fontWeight: 700,
                                        }}
                                    >
                                        Turn year {year}
                                        {restrictToPreferred
                                            ? ` · ${rows.length} project${
                                                  rows.length === 1 ? '' : 's'
                                              }`
                                            : ''}
                                    </Typography>
                                )}
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    style={{
                                        marginTop: lockYear
                                            ? 0
                                            : theme.spacing(1.5),
                                    }}
                                >
                                    Duplicates last year’s turn template onto
                                    empty bulk-created projects. Work window
                                    pins to project start and keeps the prior
                                    duration. Property managers confirm units,
                                    dates, and vendors in the confirm wizard.
                                </Typography>
                            </Box>

                            <Box
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'auto',
                                    padding: theme.spacing(0, 2, 2),
                                }}
                            >
                                {candidatesQuery.isLoading ? (
                                    <Box
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            padding: theme.spacing(4),
                                        }}
                                    >
                                        <CircularProgress size={32} />
                                    </Box>
                                ) : candidatesQuery.isError ? (
                                    <Typography
                                        color="error"
                                        style={{ marginTop: theme.spacing(2) }}
                                    >
                                        Failed to load schedule candidates
                                    </Typography>
                                ) : rows.length === 0 ? (
                                    <Typography
                                        color="textSecondary"
                                        style={{ marginTop: theme.spacing(2) }}
                                    >
                                        No empty bulk-created projects for{' '}
                                        {year}. Create projects first, or they
                                        may already be scheduled.
                                    </Typography>
                                ) : (
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={
                                                            allEligibleChecked
                                                        }
                                                        indeterminate={
                                                            selectedCount > 0 &&
                                                            !allEligibleChecked
                                                        }
                                                        onChange={(e) =>
                                                            toggleAll(
                                                                e.target
                                                                    .checked,
                                                            )
                                                        }
                                                        disabled={
                                                            eligibleCount === 0
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>Property</TableCell>
                                                <TableCell>Project</TableCell>
                                                <TableCell>
                                                    Source turn
                                                </TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {rows.map((row) => {
                                                const sourceId =
                                                    sourceByTarget[row.id] ??
                                                    defaultSourceId(row)
                                                const selectable = isSelectable(
                                                    row,
                                                )
                                                const reason =
                                                    row.source_status ===
                                                    'no_source'
                                                        ? 'No prior turn with included units near last year'
                                                        : null

                                                return (
                                                    <TableRow
                                                        key={row.id}
                                                        hover={selectable}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                checked={selectedIds.has(
                                                                    row.id,
                                                                )}
                                                                disabled={
                                                                    !selectable
                                                                }
                                                                onChange={(e) =>
                                                                    toggleRow(
                                                                        row.id,
                                                                        e.target
                                                                            .checked,
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.apartment_name}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {row.name}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                            >
                                                                {formatDate(
                                                                    row.start_date,
                                                                )}{' '}
                                                                –{' '}
                                                                {formatDate(
                                                                    row.end_date,
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell
                                                            style={{
                                                                minWidth: 220,
                                                            }}
                                                        >
                                                            {row.sources
                                                                .length ===
                                                            0 ? (
                                                                <Typography
                                                                    variant="body2"
                                                                    color="textSecondary"
                                                                >
                                                                    —
                                                                </Typography>
                                                            ) : row.sources
                                                                  .length ===
                                                              1 ? (
                                                                <Typography variant="body2">
                                                                    {
                                                                        row
                                                                            .sources[0]
                                                                            .name
                                                                    }
                                                                </Typography>
                                                            ) : (
                                                                <TextField
                                                                    select
                                                                    value={
                                                                        sourceId ??
                                                                        ''
                                                                    }
                                                                    onChange={(
                                                                        e,
                                                                    ) => {
                                                                        const nextId = Number(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                        setSourceByTarget(
                                                                            (
                                                                                prev,
                                                                            ) => ({
                                                                                ...prev,
                                                                                [row.id]: nextId,
                                                                            }),
                                                                        )
                                                                    }}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    fullWidth
                                                                    disabled={
                                                                        phase !==
                                                                        'form'
                                                                    }
                                                                >
                                                                    {row.sources.map(
                                                                        (
                                                                            source,
                                                                        ) => (
                                                                            <MenuItem
                                                                                key={
                                                                                    source.id
                                                                                }
                                                                                value={
                                                                                    source.id
                                                                                }
                                                                            >
                                                                                {
                                                                                    source.name
                                                                                }
                                                                            </MenuItem>
                                                                        ),
                                                                    )}
                                                                </TextField>
                                                            )}
                                                            {reason && (
                                                                <Typography
                                                                    variant="caption"
                                                                    color="textSecondary"
                                                                    display="block"
                                                                >
                                                                    {reason}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {statusLabel(row)}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                )}
                            </Box>

                            <Container
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: theme.spacing(1),
                                    padding: theme.spacing(1.5, 2),
                                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    style={{ fontWeight: 700 }}
                                >
                                    {selectedCount} selected · {eligibleCount}{' '}
                                    eligible
                                </Typography>
                                <Box
                                    style={{
                                        display: 'flex',
                                        gap: theme.spacing(1),
                                    }}
                                >
                                    <Button onClick={handleClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSchedule}
                                        disabled={
                                            selectedCount === 0 ||
                                            candidatesQuery.isLoading
                                        }
                                    >
                                        Schedule Projects
                                    </Button>
                                </Box>
                            </Container>
                        </>
                    ) : (
                        <>
                            <Box
                                style={{
                                    padding: theme.spacing(2),
                                    borderBottom:
                                        '1px solid rgba(0, 0, 0, 0.08)',
                                    flexShrink: 0,
                                }}
                            >
                                {phase === 'failed' ? (
                                    <Typography color="error">
                                        {failureMessage ??
                                            'Bulk schedule failed'}
                                    </Typography>
                                ) : (
                                    <>
                                        <Typography
                                            variant="subtitle1"
                                            style={{ fontWeight: 700 }}
                                        >
                                            {phase === 'done'
                                                ? 'Scheduling complete'
                                                : 'Scheduling projects…'}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="textSecondary"
                                            style={{
                                                marginTop: theme.spacing(0.5),
                                            }}
                                        >
                                            {phase === 'running' &&
                                            currentApartmentName
                                                ? `Working on ${currentApartmentName}`
                                                : `${progressCurrent} of ${progressTotal}`}
                                        </Typography>
                                        <Box
                                            style={{
                                                marginTop: theme.spacing(1.5),
                                            }}
                                        >
                                            <LinearProgress
                                                variant="determinate"
                                                value={
                                                    phase === 'done'
                                                        ? 100
                                                        : progressPercent
                                                }
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>

                            <Box
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: theme.spacing(1.5),
                                    padding: theme.spacing(2),
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        style={{
                                            fontWeight: 700,
                                            padding: theme.spacing(1, 1.5),
                                            borderBottom:
                                                '1px solid rgba(0, 0, 0, 0.08)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        Scheduled ({scheduled.length})
                                    </Typography>
                                    <Box
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            padding: theme.spacing(1, 1.5),
                                        }}
                                    >
                                        {scheduled.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {phase === 'running'
                                                    ? 'Waiting…'
                                                    : 'None'}
                                            </Typography>
                                        ) : (
                                            scheduled.map((item) => (
                                                <Typography
                                                    key={item.schedule_id}
                                                    variant="body2"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {item.name ??
                                                        item.apartment_name}
                                                    {item.source_name
                                                        ? ` ← ${item.source_name}`
                                                        : ''}
                                                </Typography>
                                            ))
                                        )}
                                    </Box>
                                </Box>

                                <Box
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        style={{
                                            fontWeight: 700,
                                            padding: theme.spacing(1, 1.5),
                                            borderBottom:
                                                '1px solid rgba(0, 0, 0, 0.08)',
                                            flexShrink: 0,
                                        }}
                                    >
                                        Skipped ({skipped.length})
                                    </Typography>
                                    <Box
                                        style={{
                                            flex: 1,
                                            minHeight: 0,
                                            overflow: 'auto',
                                            padding: theme.spacing(1, 1.5),
                                        }}
                                    >
                                        {skipped.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {phase === 'running'
                                                    ? 'Waiting…'
                                                    : 'None'}
                                            </Typography>
                                        ) : (
                                            skipped.map((item, idx) => (
                                                <Typography
                                                    key={`${item.schedule_id}-${idx}`}
                                                    variant="body2"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {item.name ??
                                                        item.apartment_name ??
                                                        `Project ${item.schedule_id}`}{' '}
                                                    — {item.reason}
                                                </Typography>
                                            ))
                                        )}
                                    </Box>
                                </Box>

                                {errors.length > 0 && (
                                    <Box
                                        style={{
                                            flex: '0 1 120px',
                                            minHeight: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            border: `1px solid ${theme.palette.error.light}`,
                                            borderRadius: 4,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                fontWeight: 700,
                                                padding: theme.spacing(1, 1.5),
                                                borderBottom: `1px solid ${theme.palette.error.light}`,
                                                color: theme.palette.error.main,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Errors ({errors.length})
                                        </Typography>
                                        <Box
                                            style={{
                                                flex: 1,
                                                minHeight: 0,
                                                overflow: 'auto',
                                                padding: theme.spacing(1, 1.5),
                                            }}
                                        >
                                            {errors.map((item, idx) => (
                                                <Typography
                                                    key={`${item.schedule_id}-${idx}`}
                                                    variant="body2"
                                                    color="error"
                                                    style={{
                                                        marginBottom: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    {item.name ??
                                                        item.apartment_name ??
                                                        `Project ${item.schedule_id}`}{' '}
                                                    — {item.error}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            <Container
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: theme.spacing(1),
                                    padding: theme.spacing(1.5, 2),
                                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                                }}
                            >
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleClose}
                                >
                                    {phase === 'running' ? 'Close' : 'Done'}
                                </Button>
                            </Container>
                        </>
                    )}
                </Paper>
            </Slide>
        </Modal>
    )
}
